// src/services/powApi.ts — Capa de servicios tipados POW-02
// Todos los endpoints del backend /api/pow/* y /api/auth/admin/*
// Uso en páginas: import { powApi } from '@/services/powApi'
import api from './api'

// Nota (LOGIN-19): la capa `authApi` era código muerto (el store llama a
// `api.post` directamente). Se eliminó para no mantener contratos paralelos.

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const dashboardApi = {
  /** KPIs globales; `anio`/`mes` opcionales filtran los indicadores del periodo. */
  kpis: (params?: { anio?: number; mes?: number }) =>
    api.get<DashboardKpis>('/api/pow/dashboard', { params }),
}

// ── Clientes ──────────────────────────────────────────────────────────────────
export const clientesApi = {
  list: (params?: { page?: number; page_size?: number; search?: string; plan?: string; estado?: string }) =>
    api.get<PaginatedList<ClienteAdmin>>('/api/pow/clientes', { params }),

  get: (id: number) => api.get<ClienteAdmin>(`/api/pow/clientes/${id}`),

  crear: (data: ClienteCreate) =>
    api.post<ClienteCrearResponse>('/api/pow/clientes', data),

  actualizar: (id: number, data: Partial<ClienteCreate>) =>
    api.put<ClienteAdmin>(`/api/pow/clientes/${id}`, data),

  inactivar: (id: number, motivo: string) =>
    api.patch<MessageResponse>(`/api/pow/clientes/${id}/inactivar`, { motivo }),

  activar: (id: number) =>
    api.patch<MessageResponse>(`/api/pow/clientes/${id}/activar`),

  accesos: (id: number, limit = 20) =>
    api.get<AccesoCliente[]>(`/api/pow/clientes/${id}/accesos`, { params: { limit } }),

  ajustarCreditos: (id: number, monto: number, concepto: string) =>
    api.post<MessageResponse>('/api/pow/creditos/ajuste', { id_cliente: id, monto, concepto }),

  empresas: (id: number) =>
    api.get<EmpresaSimple[]>(`/api/pow/clientes/${id}/empresas`),

  empresaEstado: (id: number, idEmpresa: number, estado: 'activo' | 'inactivo') =>
    api.patch<MessageResponse>(`/api/pow/clientes/${id}/empresas/${idEmpresa}/estado`, { estado }),

  eliminarEmpresa: (id: number, idEmpresa: number, contrasena: string) =>
    api.delete<MessageResponse>(`/api/pow/clientes/${id}/empresas/${idEmpresa}`, { data: { contrasena } }),
}

// ── Accesos (bitácora global de clientes) ─────────────────────────────────────
export const accesosApi = {
  list: (params?: { page?: number; page_size?: number; search?: string; desde?: string; hasta?: string }) =>
    api.get<PaginatedList<AccesoListado>>('/api/pow/accesos', { params }),
}

// ── Pagos ─────────────────────────────────────────────────────────────────────
export const pagosApi = {
  list: (params?: { page?: number; page_size?: number; mes?: number; anio?: number; vista?: string }) =>
    api.get<PaginatedList<PagoAdmin>>('/api/pow/pagos', { params }),

  kpis: () => api.get<PagosKpis>('/api/pow/pagos/kpis'),

  detalle: (id: number) => api.get<PagoDetalle>(`/api/pow/pagos/${id}`),
}

// ── Comprobantes ──────────────────────────────────────────────────────────────

/** Descarga autenticada de un archivo (blob) disparando la descarga en el navegador. */
async function descargarArchivo(url: string, filename: string): Promise<void> {
  const res = await api.get(url, { responseType: 'blob' })
  const blobUrl = window.URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(blobUrl)
}

export const comprobantesApi = {
  list: (params?: { page?: number; page_size?: number; id_cliente?: number; estado_sunat?: string; solo_referenciables?: boolean; q?: string }) =>
    api.get<PaginatedList<ComprobanteAdmin>>('/api/pow/comprobantes', { params }),

  detalle: (id: number) => api.get<ComprobanteDetalle>(`/api/pow/comprobantes/${id}`),

  emitirManual: (data: ComprobanteManualPayload) =>
    api.post<ComprobanteManualResponse>('/api/pow/comprobantes/manual', data),

  downloadPdf: (id: number, filename?: string) =>
    descargarArchivo(`/api/pow/comprobantes/${id}/pdf`, filename ?? `comprobante-${id}.pdf`),

  downloadXml: (id: number, filename?: string) =>
    descargarArchivo(`/api/pow/comprobantes/${id}/xml`, filename ?? `comprobante-${id}.xml`),
}

// ── Tipo de Cambio ────────────────────────────────────────────────────────────
export interface TipoCambio {
  id: number
  fecha: string
  moneda_origen: string
  moneda_destino: string
  compra: number
  venta: number
  estado: string
}

export interface TipoCambioFiltros {
  moneda_origen?: string
  moneda_destino?: string
  desde?: string
  hasta?: string
  estado?: string
}

export const tipoCambioApi = {
  list: (params?: TipoCambioFiltros) => api.get<TipoCambio[]>('/api/pow/tipo-cambio', { params }),
  create: (data: Omit<TipoCambio, 'id'>) => api.post<TipoCambio>('/api/pow/tipo-cambio', data),
  update: (id: number, data: Omit<TipoCambio, 'id'>) => api.put<TipoCambio>(`/api/pow/tipo-cambio/${id}`, data),
  delete: (id: number) => api.delete<MessageResponse>(`/api/pow/tipo-cambio/${id}`)
}

// ── Planes ────────────────────────────────────────────────────────────────────
export const planesApi = {
  list: () => api.get<Plan[]>('/api/pow/planes'),

  update: (id: string, data: Partial<Plan>) =>
    api.patch<Plan>(`/api/pow/planes/${id}`, data),
}


// ── Cupones v2 ────────────────────────────────────────────────────────────────
export const cuponesApi = {
  listar: () => api.get<Cupon[]>('/api/pow/cupones'),

  get: (id: number) => api.get<Cupon>(`/api/pow/cupones/${id}`),

  crear: (data: CuponCreate) =>
    api.post<Cupon>('/api/pow/cupones', data),

  actualizar: (id: number, data: CuponUpdate) =>
    api.patch<Cupon>(`/api/pow/cupones/${id}`, data),

  inactivar: (id: number) =>
    api.patch<MessageResponse>(`/api/pow/cupones/${id}/inactivar`),

  activar: (id: number) =>
    api.patch<MessageResponse>(`/api/pow/cupones/${id}/activar`),

  getHistorial: (id: number, skip = 0, limit = 50) =>
    api.get<CuponUsoItem[]>(`/api/pow/cupones/${id}/usos`, { params: { skip, limit } }),

  // Legacy aliases (compatibilidad con código existente)
  list: () => api.get<Cupon[]>('/api/pow/cupones'),
  create: (data: CuponCreate) => api.post<Cupon>('/api/pow/cupones', data),
  update: (id: number, data: CuponUpdate) => api.patch<Cupon>(`/api/pow/cupones/${id}`, data),
}

// ── Costo IA ──────────────────────────────────────────────────────────────────
export const costoIaApi = {
  listar: (params?: { fecha_desde?: string; fecha_hasta?: string; id_cliente?: number; id_plan?: number }) =>
    api.get<CostoIaPeriodo[]>('/api/pow/reportes/costo-ia', { params }),
}

// ── Usuarios admin ────────────────────────────────────────────────────────────
export const usuariosApi = {
  list: () => api.get<UsuarioAdminResponse[]>('/api/pow/usuarios'),

  create: (data: UsuarioAdminCreate) =>
    api.post<UsuarioAdminResponse>('/api/pow/usuarios', data),

  update: (id: number, data: Partial<UsuarioAdminCreate> & { estado?: string }) =>
    api.patch<UsuarioAdminResponse>(`/api/pow/usuarios/${id}`, data),

  inactivar: (id: number) =>
    api.patch<MessageResponse>(`/api/pow/usuarios/${id}/inactivar`),

  activar: (id: number) =>
    api.patch<MessageResponse>(`/api/pow/usuarios/${id}/activar`),
}

// ── Roles ─────────────────────────────────────────────────────────────────────
export const rolesApi = {
  list: () => api.get<RolAdmin[]>('/api/pow/roles'),

  create: (data: RolAdminPayload) =>
    api.post<RolAdmin>('/api/pow/roles', data),

  update: (id: number, data: RolAdminPayload) =>
    api.patch<RolAdmin>(`/api/pow/roles/${id}`, data),

  remove: (id: number) => api.delete<MessageResponse>(`/api/pow/roles/${id}`),

  get: (id: number) => api.get<RolAdmin>(`/api/pow/roles/${id}`),
}

// ── Catálogos maestros ────────────────────────────────────────────────────────
export const catalogoApi = {
  paises: () => api.get<CatalogoPais[]>('/api/pow/catalogo/paises'),
  monedas: () => api.get<CatalogoMoneda[]>('/api/pow/catalogo/monedas'),
  planes: () => api.get<CatalogoPlanes[]>('/api/pow/catalogo/planes'),
  documentosIdentidad: () => api.get<CatalogoDocumento[]>('/api/pow/catalogo/documentos-identidad'),
  formasPago: () => api.get<{ id: number; nombre: string }[]>('/api/pow/catalogo/formas-pago'),
  familiasServicio: () => api.get<{ id: number; nombre: string }[]>('/api/pow/catalogo/familias-servicio'),
  seriesFacturacion: () => api.get<SerieFacturacionOption[]>('/api/pow/catalogo/series-facturacion'),
  servicios: () => api.get<ServicioCatalogo[]>('/api/pow/catalogo/servicios'),
  sectores: () => api.get<{ id: number; sector: string; nombre: string }[]>('/api/pow/catalogo/sectores'),
}

// ── Reportes ──────────────────────────────────────────────────────────────────
// Contrato de detalle Fase 1: WorkSpec/reportes/MODULO_REPORTES.md §3.3, §3.4 y §4.
// El backend se implementa en paralelo con este mismo contrato; aquí solo se
// declaran los tipos/JSON documentados (el catálogo no depende del resumen).

/** Claves de los reportes con endpoint y página disponibles. */
export type ReporteClave =
  | 'consumo-ia'
  | 'facturacion'
  | 'cupones'
  | 'pagos'
  | 'clientes'
  | 'cuentas-inactivas'
  | 'planes'
  | 'bonificaciones'
  | 'procesos'
  | 'usuarios'

export type GranoReporte = 'dia' | 'mes'

/** `null` = reporte multi-moneda con desglose en stats/items. */
export type MonedaReporte = 'USD' | 'PEN' | null

export interface ReportePeriodoContrato {
  desde: string
  hasta: string
  etiqueta: string
  grano: GranoReporte
  modo: string
  zona_horaria: string
}

export interface ReporteSeriePunto<TValores> {
  periodo: string
  etiqueta: string
  valores: TValores
}

/** Respuesta común de los `GET /api/pow/reportes/{clave}` (§3.3). */
export interface ReporteRespuesta<TStats, TItem, TValores> {
  reporte: string
  titulo: string
  descripcion: string
  fuente: string
  moneda: MonedaReporte
  periodo: ReportePeriodoContrato
  generado_en: string
  stats: TStats
  series: ReporteSeriePunto<TValores>[]
  items: TItem[]
  total: number
  truncado: boolean
  filtros: Record<string, unknown>
  /**
   * Sección opcional para datos ricos que no caben en `items` (§14, Fase 3).
   * Los reportes anteriores no lo envían (o `null`). Cada página lo tipa con
   * su propio shape (`PlanDistribucionExtras`, ...).
   */
  extras?: Record<string, unknown> | null
}

/** Parámetros de periodo comunes (§3.3). */
export interface ReporteParamsPeriodo {
  anio?: number
  mes?: number
  desde?: string
  hasta?: string
  grano?: GranoReporte
}

// ── Reporte: Consumo IA (§4.1) ────────────────────────────────────────────────
export interface ConsumoIaReporteItem {
  id_cliente: number
  cliente: string
  correo: string
  tokens_entrada: number
  tokens_salida: number
  tokens_total: number
  costo_usd: number
  periodos: number
  modelos: string[]
  ultima_fecha: string | null
}

export interface ConsumoIaReporteStats {
  costo_usd_total: number
  tokens_total: number
  clientes_con_consumo: number
  periodos_con_consumo: number
  /** Nombres de modelo o conteo, según el backend; la UI lo normaliza. */
  modelos: string[] | number
  costo_promedio_periodo_usd: number
}

export interface ConsumoIaSerieValores {
  costo_usd: number
  tokens_total: number
}

export type ConsumoIaReporte = ReporteRespuesta<
  ConsumoIaReporteStats,
  ConsumoIaReporteItem,
  ConsumoIaSerieValores
>

export interface ConsumoIaFiltros extends ReporteParamsPeriodo {
  id_cliente?: number
  id_plan?: number
}

// ── Reporte: Facturación (§4.2) ───────────────────────────────────────────────
export interface FacturacionReporteItem {
  id: number
  fecha_emision: string
  tipo: string
  serie_correlativo: string
  cliente: string
  nro_doc: string
  moneda: string
  subtotal: number
  descuento: number
  igv: number
  total: number
  estado_sunat: string
}

export interface FacturacionReporteStats {
  emitidos: number
  aceptados: number
  aceptados_obs: number
  rechazados: number
  anulados: number
  borradores: number
  facturado_usd: number
  facturado_pen: number
  nc_usd: number
  nc_pen: number
  nd_usd: number
  nd_pen: number
  neto_usd: number
  neto_pen: number
  igv_usd: number
  igv_pen: number
  descuento_usd: number
  descuento_pen: number
}

export interface FacturacionSerieValores {
  emitidos: number
  facturado_usd: number
  facturado_pen: number
}

export type FacturacionReporte = ReporteRespuesta<
  FacturacionReporteStats,
  FacturacionReporteItem,
  FacturacionSerieValores
>

export interface FacturacionFiltros extends ReporteParamsPeriodo {
  tipo?: string
  estado_sunat?: string
  moneda?: 'PEN' | 'USD'
}

// ── Reporte: Cupones (§4.3) ───────────────────────────────────────────────────
export interface CuponesReporteItem {
  id_cupon: number
  codigo: string
  tipo_modalidad: string
  valor: number
  tipo_cupon: string
  plan: string | null
  fecha_inicio: string
  fecha_fin: string
  estado: string
  caducado: boolean
  usos: number
  descuento_total: number
  monto_original_total: number
  monto_final_total: number
  ultima_uso: string | null
}

export interface CuponesReporteStats {
  cupones_total: number
  activos: number
  caducados: number
  inactivos: number
  usos: number
  cupones_usados: number
  descuento_total: number
  monto_original_total: number
  monto_final_total: number
}

export interface CuponesSerieValores {
  usos: number
  descuento_total: number
}

export type CuponesReporte = ReporteRespuesta<
  CuponesReporteStats,
  CuponesReporteItem,
  CuponesSerieValores
>

export interface CuponesFiltros extends ReporteParamsPeriodo {
  estado?: 'todos' | 'activo' | 'caducado' | 'inactivo'
  tipo_modalidad?: 'porcentaje' | 'monto' | 'creditos'
  q?: string
}

// ── Reporte: Conversión de pagos (§4.4) ───────────────────────────────────────
export interface PagosReporteItem {
  periodo: string
  etiqueta: string
  sesiones: number
  confirmados: number
  denegados: number
  expirados: number
  abandonadas: number
  en_curso: number
  tasa_conversion: number
  monto_confirmado_usd: number
  ticket_promedio_usd: number
}

export interface PagosReporteStats {
  sesiones: number
  confirmados: number
  tasa_conversion: number
  monto_confirmado_usd: number
  ticket_promedio_usd: number
  denegados: number
  expirados: number
  abandonadas: number
  en_curso: number
  otros_pagos: number
  otros_monto_usd: number
}

export interface PagosSerieValores {
  confirmados: number
  sesiones: number
  tasa_conversion: number
}

export type PagosReporte = ReporteRespuesta<
  PagosReporteStats,
  PagosReporteItem,
  PagosSerieValores
>

// ── Reporte: Crecimiento de cuentas / altas (§4.5, v2 §14.1) ──────────────────
// v2: altas/bajas/reactivaciones salen de `t_cliente_evento`; `neto` =
// altas + reactivaciones − bajas. El stock sigue a la fecha de generación.
export interface ClientesReporteItem {
  periodo: string
  etiqueta: string
  altas: number
  bajas: number
  reactivaciones: number
  neto: number
}

export interface ClientesReporteStats {
  altas: number
  bajas: number
  reactivaciones: number
  /** `altas + reactivaciones − bajas` en el periodo. */
  crecimiento_neto: number
  altas_promedio_mes: number
  cuentas_totales: number
  cuentas_activas: number
  cuentas_inactivas: number
  empresas: number
  nuevas_ultimos_30_dias: number
  /** Primera fecha con eventos en `t_cliente_evento` (ISO o null). */
  cobertura_eventos_desde: string | null
  /** Metodología y limitaciones de la cobertura de eventos. */
  nota: string
}

export interface ClientesSerieValores {
  altas: number
  bajas: number
  reactivaciones: number
  neto: number
}

export type ClientesReporte = ReporteRespuesta<
  ClientesReporteStats,
  ClientesReporteItem,
  ClientesSerieValores
>

// ── Reporte: Adopción por plan y migraciones (§14.2) ──────────────────────────
export interface PlanDistribucionItem {
  id_plan: number
  plan: string
  empresas: number
  clientes: number
  /** Porcentaje (0–100) sobre las empresas con plan vigente al cierre. */
  pct_empresas: number
  migraciones_entrada: number
  migraciones_salida: number
}

export interface PlanMigracionItem {
  fecha: string
  id_empresa: number
  empresa: string | null
  id_cliente: number | null
  cliente: string | null
  plan_anterior: string | null
  plan_nuevo: string | null
  motivo: string | null
}

export interface PlanesReporteStats {
  empresas_totales: number
  empresas_con_plan: number
  clientes_con_plan: number
  planes_activos: number
  migraciones_periodo: number
  migraciones_entrada_total: number
  plan_top: string | null
  /** Mínima `fecha_desde` del historial de plan (ISO o null). */
  cobertura_desde: string | null
  nota: string
}

export interface PlanesSerieValores {
  empresas_con_plan: number
  migraciones: number
}

/** `extras` de `/reportes/planes` (§14.2): migraciones observadas + cobertura. */
export interface PlanesReporteExtras {
  migraciones: PlanMigracionItem[]
  cobertura: string
  nota: string
}

export type PlanesReporte = ReporteRespuesta<
  PlanesReporteStats,
  PlanDistribucionItem,
  PlanesSerieValores
>

// ── Reporte: exportación (§3.4) ───────────────────────────────────────────────
export interface ReporteExportacion {
  reporte: string
  periodo: ReportePeriodoContrato
  filtros: Record<string, unknown>
  moneda: MonedaReporte
  fuente: string
  generado_en: string
  filas: Record<string, unknown>[]
  total: number
  truncado: boolean
}

// ── Reporte: Cuentas inactivas 30/60/90 (Fase 2/3) ────────────────────────────
// Foto al día de hoy (sin parámetros de periodo): una fila por cuenta MIVA con
// su última actividad, días sin actividad y bucket. Actividad = acceso, pago
// confirmado, movimiento de crédito o periodo procesado; línea base fecha_alta.
export type BucketInactividad = 'nuevo' | 'activo' | '30' | '60' | '90'

export interface CuentaInactivaItem {
  id_cliente: number
  cliente: string
  correo: string
  estado_comercial: string
  fecha_alta: string | null
  ultima_actividad: string | null
  dias_sin_actividad: number | null
  bucket: BucketInactividad
  sin_actividad_registrada: boolean
  historia_insuficiente: boolean
  ultimo_acceso: string | null
  ultimo_pago: string | null
  ultimo_movimiento: string | null
  ultimo_periodo: string | null
}

export interface CuentasInactivasStats {
  cuentas_totales: number
  cuentas_activas: number
  cuentas_inactivas: number
  inactivas_30: number
  inactivas_60: number
  inactivas_90: number
  sin_actividad_registrada: number
  historia_insuficiente: number
  nuevas: number
  dias_referencia: number
  nota: string
}

export interface CuentasInactivasSerieValores {
  cuentas: number
}

export type CuentasInactivasReporte = ReporteRespuesta<
  CuentasInactivasStats,
  CuentaInactivaItem,
  CuentasInactivasSerieValores
>

export interface CuentasInactivasFiltros {
  /** Umbral de inactividad: 30, 60 o 90 días (opcional; por defecto todos). */
  dias?: 30 | 60 | 90
  /** Estado comercial de la cuenta (opcional; por defecto todos). */
  estado?: 'activo' | 'inactivo' | 'todos'
}

// ── Reporte: Actividad de administradores (§15.1) ─────────────────────────────
export interface UsuarioActividadItem {
  id_usuario_admin: number
  usuario: string
  correo: string
  rol: string | null
  acciones: number
  sesiones: number
  sesiones_2fa_fallidas: number
  ultima_actividad: string | null
  ultimo_acceso: string | null
}

export interface UsuariosReporteStats {
  usuarios_con_actividad: number
  usuarios_activos: number
  acciones_total: number
  sesiones_total: number
  sesiones_2fa_fallidas: number
  ultima_actividad: string | null
  /** Mínima fecha registrada en la auditoría del periodo (ISO o null). */
  cobertura_desde: string | null
  nota: string
}

export interface UsuariosSerieValores {
  acciones: number
  sesiones: number
}

export interface UsuarioUltimaAccionItem {
  fecha: string
  usuario: string | null
  accion: string
  modulo: string | null
  ip: string | null
}

/** `extras` de `/reportes/usuarios` (§15.1). */
export interface UsuariosReporteExtras {
  ultimas_acciones: UsuarioUltimaAccionItem[]
  nota: string
}

export type UsuariosReporte = ReporteRespuesta<
  UsuariosReporteStats,
  UsuarioActividadItem,
  UsuariosSerieValores
>

// ── Reporte: Salud de procesamiento (§15.2) ───────────────────────────────────
export interface ProcesoEventoItem {
  id: number
  fecha_evento: string | null
  anio: number
  mes: number
  empresa: string | null
  cliente: string | null
  plan: string | null
  tipo_evento: string | null
  estado: string
  nro_version: string | number | null
  /** Duración = fecha_evento − version.created_at (incluye espera de cola). */
  duracion_seg: number | null
}

export interface ProcesosReporteStats {
  eventos: number
  procesados: number
  fallidos: number
  /** % de fallidos sobre eventos (1 decimal). */
  tasa_fallo: number
  tiempo_promedio_seg: number
  tiempo_p95_seg: number
  periodos_unicos: number
  reprocesos: number
  nota: string
}

export interface ProcesosSerieValores {
  procesados: number
  fallidos: number
  tiempo_promedio_seg: number
}

export interface ProcesosPorTipoItem {
  tipo_evento: string
  eventos: number
  fallidos: number
}

/** `extras` de `/reportes/procesos` (§15.2). */
export interface ProcesosReporteExtras {
  por_tipo: ProcesosPorTipoItem[]
  nota?: string
}

export interface ProcesosFiltros extends ReporteParamsPeriodo {
  estado?: 'procesado' | 'error' | 'todos'
  tipo_evento?: string
}

export type ProcesosReporte = ReporteRespuesta<
  ProcesosReporteStats,
  ProcesoEventoItem,
  ProcesosSerieValores
>

// ── Reporte: Bonificaciones y créditos gratis (§15.3) ─────────────────────────
export interface BonificacionClienteItem {
  id_cliente: number
  cliente: string
  correo: string
  creditos_otorgados: number
  creditos_sin_precio: number
  /** Valorización referencial (créditos × precio_credito_usd del plan). */
  valorizado_usd: number
  movimientos: number
  motivos: string[]
  ultima_fecha: string | null
}

export interface BonificacionesReporteStats {
  creditos_otorgados: number
  valorizado_usd: number
  clientes_beneficiados: number
  movimientos: number
  creditos_sin_precio: number
  reemplazos_otorgados: number
  reemplazos_creditos: number
  cobertura_desde: string | null
  nota: string
}

export interface BonificacionesSerieValores {
  creditos: number
  valorizado_usd: number
}

export interface BonificacionPorMotivoItem {
  motivo: string
  creditos: number
  movimientos: number
  valorizado_usd: number
}

export interface BonificacionReemplazoItem {
  fecha: string
  cliente: string | null
  empresa: string | null
  cantidad: number
  motivo: string | null
}

/** `extras` de `/reportes/bonificaciones` (§15.3). */
export interface BonificacionesReporteExtras {
  por_motivo: BonificacionPorMotivoItem[]
  reemplazos: BonificacionReemplazoItem[]
  nota?: string
}

export interface BonificacionesFiltros extends ReporteParamsPeriodo {
  id_cliente?: number
}

export type BonificacionesReporte = ReporteRespuesta<
  BonificacionesReporteStats,
  BonificacionClienteItem,
  BonificacionesSerieValores
>

// ── Resumen legacy (Fase 0) — el catálogo ya no lo consume ────────────────────
export interface ReporteChartItem { name: string; value: number }

export interface ReporteBloque {
  chartData: ReporteChartItem[]
  stats: Record<string, number>
}

export interface ReportesResumen {
  crecimiento: ReporteBloque
  conversion: ReporteBloque
  ia: ReporteBloque
  cupones: ReporteBloque
  facturacion: ReporteBloque
  procesos: ReporteBloque
}

export const reportesApi = {
  /** Resumen agregado legacy de las 6 tarjetas (se mantiene por compatibilidad). */
  resumen: () => api.get<ReportesResumen>('/api/pow/reportes/resumen'),

  /** Consumo IA por cuenta MIVA (§4.1). */
  consumoIa: (params?: ConsumoIaFiltros) =>
    api.get<ConsumoIaReporte>('/api/pow/reportes/consumo-ia', { params }),

  /** Facturación electrónica multi-moneda (§4.2). */
  facturacion: (params?: FacturacionFiltros) =>
    api.get<FacturacionReporte>('/api/pow/reportes/facturacion', { params }),

  /** Cupones y promociones (§4.3). */
  cupones: (params?: CuponesFiltros) =>
    api.get<CuponesReporte>('/api/pow/reportes/cupones', { params }),

  /** Conversión de pagos Niubiz (§4.4). */
  pagos: (params?: ReporteParamsPeriodo) =>
    api.get<PagosReporte>('/api/pow/reportes/pagos', { params }),

  /** Crecimiento de cuentas MIVA / altas (§4.5). */
  clientes: (params?: ReporteParamsPeriodo) =>
    api.get<ClientesReporte>('/api/pow/reportes/clientes', { params }),

  /** Cuentas inactivas 30/60/90 (foto al día de hoy, Fase 2/3). */
  cuentasInactivas: (params?: CuentasInactivasFiltros) =>
    api.get<CuentasInactivasReporte>('/api/pow/reportes/cuentas-inactivas', { params }),

  /** Adopción por plan y migraciones observadas (Fase 3, §14.2). */
  planes: (params?: ReporteParamsPeriodo) =>
    api.get<PlanesReporte>('/api/pow/reportes/planes', { params }),

  /** Actividad de administradores (§15.1). */
  usuariosAdmin: (params?: ReporteParamsPeriodo) =>
    api.get<UsuariosReporte>('/api/pow/reportes/usuarios', { params }),

  /** Salud de procesamiento por evento de carga (§15.2). */
  procesos: (params?: ProcesosFiltros) =>
    api.get<ProcesosReporte>('/api/pow/reportes/procesos', { params }),

  /** Bonificaciones y créditos gratis valorizados (§15.3). */
  bonificaciones: (params?: BonificacionesFiltros) =>
    api.get<BonificacionesReporte>('/api/pow/reportes/bonificaciones', { params }),

  /** Export server-side gateado por `reportes.exportar` (§3.4). */
  exportar: (clave: ReporteClave, params?: ReporteParamsPeriodo & Record<string, unknown>) =>
    api.get<ReporteExportacion>(`/api/pow/reportes/${clave}/export`, { params }),
}

// ── Auditoría ─────────────────────────────────────────────────────────────────
export const auditoriaApi = {
  list: (params?: { page?: number; admin_id?: number }) =>
    api.get<PaginatedList<AuditoriaEntry>>('/api/pow/auditoria', { params }),
}

// ── Historial de Cargas ───────────────────────────────────────────────────────
export interface HistorialCargasFiltros {
  id_cliente?: number
  id_empresa?: number
  id_plan?: number
  anio?: number
  mes?: number
  estado_resultado?: string
  tipo_evento?: 'carga_inicial' | 'reemplazo' | 'reproceso' | string
  search?: string
}

export interface HistorialCargasListParams extends HistorialCargasFiltros {
  page?: number
  page_size?: number
}

export const historialCargasApi = {
  list: (params?: HistorialCargasListParams) =>
    api.get<PaginatedList<HistorialEventoListResponse>>('/api/pow/historial-cargas', { params }),

  // Export sin paginar: todos los registros que cumplen los filtros.
  exportar: (params?: HistorialCargasFiltros) =>
    api.get<HistorialEventoListResponse[]>('/api/pow/historial-cargas/export', { params }),

  get: (id: number) =>
    api.get<HistorialEventoDetalleResponse>(`/api/pow/historial-cargas/${id}`),

  /** URL pre-firmada (S3) para descargar un archivo del evento. */
  archivoUrl: (idEvento: number, idArchivo: number) =>
    api.get<{ url: string; expira_en_segundos: number }>(
      `/api/pow/historial-cargas/${idEvento}/archivos/${idArchivo}/url-descarga`,
    ),

  /** Descarga autenticada del ZIP con todos los archivos del evento. */
  descargarArchivosZip: (idEvento: number) =>
    descargarArchivo(
      `/api/pow/historial-cargas/${idEvento}/archivos/zip`,
      `archivos_evento_${idEvento}.zip`,
    ),
}


// ── Types ──────────────────────────────────────────────────────────────────────
export interface AdminMeResponse {
  id: number; nombre: string; email: string; rol: string
  es_superadmin?: boolean
  permisos: PermisosMap
}

export interface DashboardKpis {
  clientes_activos: number; total_clientes?: number; churn_rate: number; nuevos_mes: number
  clientes_inactivos?: number
  ingresos_mes: number; ingresos_anio: number; tokens_ia: number; informes_generados: number
  ingresos_mes_usd?: number; ingresos_anio_usd?: number; total_empresas?: number; tasa_churn?: number
  tokens_ia_total?: number; periodos_procesados_total?: number
  ingresos_total?: number
  ingresos_periodo?: number
  costo_ia_total?: number
  costo_ia_periodo?: number
  informes_periodo?: number
  periodo?: { anio: number; mes: number }
  clientes_por_plan?: { id_plan: number; nombre: string; clientes: number }[]
  informes_por_plan?: { id_plan: number; nombre: string; informes: number }[]
  rango?: DashboardRango
  evolucion_ingresos?: DashboardEvolucionIngresos[]
  evolucion_ia?: DashboardEvolucionIa[]
  top_clientes_periodo?: DashboardTopCliente[]
}

export type DashboardModo = 'mes' | 'anio' | 'rango'

export interface DashboardRango {
  desde: string
  hasta: string
  etiqueta: string
  grano: 'dia' | 'mes'
  modo: DashboardModo
}

export interface DashboardEvolucionIngresos {
  periodo: string
  etiqueta: string
  anio: number
  mes_num: number
  ingresos_mes: number
  pagos: number
  nuevos: number
}

export interface DashboardEvolucionIa {
  periodo: string
  etiqueta: string
  tokens_entrada: number
  tokens_salida: number
  tokens_total: number
  costo_usd: number
}

export interface DashboardTopCliente {
  id_cliente: number
  nombre: string
  ingresos_usd: number
  pagos: number
  participacion_pct: number
}

export interface PaginatedList<T> {
  items: T[]; total: number; page: number; page_size: number; pages?: number
}

export interface MessageResponse { mensaje?: string; message?: string; ok?: boolean }

// Cliente del backend (campos reales)
export interface ClienteAdmin {
  id: number
  nombre_comercial: string
  contacto?: string
  correo: string
  telefono?: string
  id_tipo_documento?: number
  nro_documento: string
  direccion?: string
  idioma_predeterminado?: string
  id_moneda_informe?: number
  saldo_creditos: number
  comprado_usd?: number
  consumido_usd?: number
  saldo_usd?: number
  id_pais?: number
  estado: string
  fecha_alta: string
  ultimo_acceso?: string
  fecha_baja?: string | null
  motivo_baja?: string | null
  empresas_count: number
}

export interface AccesoCliente {
  id: number
  fecha: string
  ip?: string | null
  user_agent?: string | null
}

export interface AccesoListado extends AccesoCliente {
  id_cliente: number
  cliente: string
  correo?: string | null
}

export interface ClienteCreate {
  nombre_comercial: string
  correo: string
  nro_documento: string
  id_tipo_documento: number
  contacto?: string
  telefono?: string
  id_pais?: number
  /** Preferencias del cliente. Vacíos → default (idioma 'ES', moneda base del sistema). */
  idioma?: string
  id_moneda_informe?: number
}

export interface ClienteCrearResponse {
  id: number
  nombre_comercial: string
  correo: string
  estado: string
  contrasena_temporal: string
  mensaje: string
}

export interface PagoAdmin {
  id: number; fecha: string; hora: string; cliente: string; concepto: string
  descuento?: string; cupon?: string | null; metodo: string; procedencia?: string; monto: number; estado: string
}

export interface PagosKpis {
  recaudacion_mes: number; recaudacion_anual: number; tasa_conversion: number; monto_promedio: number
  conteo_confirmados?: number; conteo_no_culminados?: number; conteo_reembolsos_disputas?: number
  iniciados?: number; delta_mes?: string
}

export interface PagoNotificacionAdmin {
  id: number
  pasarela: string
  evento: string
  estado: string
  id_transaccion_pasarela?: string | null
  ip_origen?: string | null
  detalle?: string | null
  fecha?: string | null
}

export interface PagoDetalle {
  id: number
  id_cliente: number
  cliente: string
  concepto: string
  descuento?: { monto: number; detalle?: string | null; id_cupon?: number | null } | null
  metodo: string
  procedencia?: string
  id_transaccion_pasarela?: string | null
  fecha?: string | null
  ip_origen?: string | null
  estado: string
  monto: number
  id_comprobante?: number | null
  notificaciones: PagoNotificacionAdmin[]
}

/** Forma real que devuelve `GET /api/pow/comprobantes` (ComprobanteResponse). */
export interface ComprobanteAdmin {
  id: number
  id_cliente: number
  tipo: string
  serie_correlativo: string
  fecha_emision: string
  subtotal: number
  igv: number
  descuento?: number
  porcentaje_impuesto?: number | null
  total: number
  estado_sunat: string
  url_pdf?: string | null
  enviado_correo?: boolean
  cliente: string
  ruc?: string | null
}

// ── Impuestos (historial de vigencias por país) ───────────────────────────────
export interface Impuesto {
  id: number
  id_pais: number
  pais?: string | null
  nombre: string
  sigla: string
  porcentaje: number
  aplica_saas: boolean
  vigencia_desde: string
  vigencia_hasta?: string | null
  estado: string
  activo: boolean
  vigente: boolean
}

export interface ImpuestoVigente {
  id_impuesto?: number | null
  porcentaje: number
  sigla?: string | null
  vigencia_desde?: string | null
  vigencia_hasta?: string | null
}

export interface ItemComprobanteManual {
  id_servicio?: number
  descripcion: string
  cantidad: number
  precio_unitario: number
  descuento?: number
  unidad_medida?: string
  tipo_igv?: number
}

export type SerieFacturacionId = 'factura' | 'boleta' | 'nota_credito' | 'nota_debito'

/** Opción de comprobante simple (Factura / Boleta). */
export interface SerieFacturacionSimple {
  id: 'factura' | 'boleta'
  tipo_comprobante: 1 | 2
  nombre: string
  serie: string
  requiere_doc_modifica: false
  descripcion: string
}

/**
 * Opción de nota (Crédito / Débito). No expone una serie fija: SUNAT/NUBEFACT exigen
 * que la serie empiece con "F" (asociada a Factura) o "B" (asociada a Boleta). La familia
 * se deriva del comprobante que se modifica y el backend resuelve la serie.
 */
export interface SerieFacturacionNota {
  id: 'nota_credito' | 'nota_debito'
  tipo_comprobante: 3 | 4
  nombre: string
  series: { factura: string; boleta: string }
  requiere_doc_modifica: true
  descripcion: string
}

export type SerieFacturacionOption = SerieFacturacionSimple | SerieFacturacionNota

export interface ServicioCatalogo {
  id: number
  nombre: string
  id_familia: number
  familia_nombre?: string | null
  codigo_unidad: string
  unidad_medida_nombre: string
  precio?: number
}

export interface MedioPagoCatalogo {
  id: number
  codigo: string
  nombre: string
  estado?: string
  activo?: boolean
}

export interface ComprobanteManualPayload {
  id_cliente: number
  id_medio_pago: number
  tipo_comprobante: 1 | 2 | 3 | 4 // 1: Factura, 2: Boleta, 3: Nota Crédito, 4: Nota Débito
  subtipo_comprobante?: string
  moneda?: 1 | 2 // 1: PEN, 2: USD
  tipo_de_cambio?: number
  items: ItemComprobanteManual[]
  enviar_correo?: boolean
  correo_destino?: string
  es_gratuito?: boolean
  observaciones?: string
  doc_modifica_tipo?: number
  doc_modifica_serie?: string
  doc_modifica_numero?: number
  motivo_nota?: number
  motivo_sustento?: string
}

export interface ComprobanteManualResponse {
  id: number
  id_cliente: number
  tipo: string
  serie: string
  correlativo: number
  serie_correlativo: string
  fecha_emision: string
  subtotal: number
  igv: number
  total: number
  moneda: string
  estado_sunat: string
  enlace_pdf?: string
  enlace_xml?: string
  enlace_cdr?: string
  enviado_correo: boolean
  cliente: string
  ruc_dni: string
  mensaje: string
  id_comprobante_ref?: number | null
  motivo_nota_codigo?: number | null
  motivo_sustento?: string | null
}

export interface ItemComprobanteDetalle {
  orden: number
  id_servicio?: number | null
  descripcion: string
  cantidad: number
  precio_unitario: number
  descuento: number
  unidad_medida?: string | null
  subtotal: number
  igv: number
  total: number
}

export interface ComprobanteDetalle {
  id: number
  id_cliente: number
  tipo: string
  serie: string
  correlativo: number
  serie_correlativo: string
  fecha_emision: string
  cliente: string
  ruc?: string | null
  tipo_doc_receptor?: string | null
  correo?: string | null
  direccion?: string | null
  moneda: string
  subtotal: number
  igv: number
  descuento: number
  porcentaje_impuesto?: number | null
  total: number
  estado_sunat: string
  mensaje_sunat?: string | null
  hash_cdr?: string | null
  enviado_correo: boolean
  tiene_pdf: boolean
  tiene_xml: boolean
  id_comprobante_ref?: number | null
  referencia?: string | null
  motivo_nota_codigo?: number | null
  motivo_sustento?: string | null
  items: ItemComprobanteDetalle[]
}

export interface Plan {
  id: number
  nombre: string
  precio_mensual?: number
  precio_12_meses?: number
  precio_24_meses?: number
  precio_credito_usd?: number
  escalas_precios?: {
    tramos: {
      rango: string
      min: number
      max: number | null
      descuento_pct: number
      precio: number
    }[]
  } | null
  tipo_precio?: string
  estado?: string
  activo?: boolean
  precio?: number
  credito12?: number
  credito24?: number
  id_servicio?: number | null
  nombre_servicio?: string | null
}

export interface Cupon {
  id: number
  codigo: string
  tipo_modalidad: 'porcentaje' | 'monto' | 'creditos'
  valor: number
  fecha_inicio: string
  fecha_fin: string
  id_plan: number | null
  nombre_plan: string
  tipo_cupon: string
  observacion: string | null
  tiene_compra_minima: boolean
  compra_minima_creditos: number
  creditos_maximo_pool: number | null
  limite_usos_total: number
  usos_consumidos: number
  limite_usos_cliente: number
  estado: string
  created_at: string
}

export interface CuponCreate {
  codigo: string
  tipo_modalidad: 'porcentaje' | 'monto' | 'creditos'
  valor: number
  fecha_inicio: string
  fecha_fin: string
  id_plan?: number | null
  tipo_cupon?: string
  observacion?: string | null
  tiene_compra_minima?: boolean
  compra_minima_creditos?: number
  creditos_maximo_pool?: number | null
  limite_usos_total?: number
  limite_usos_cliente?: number
}

export interface CuponUpdate {
  fecha_inicio?: string
  fecha_fin?: string
  limite_usos_total?: number
  limite_usos_cliente?: number
  tipo_cupon?: string
  observacion?: string | null
  estado?: 'activo' | 'inactivo'
}

export interface CuponUsoItem {
  id: number
  id_cliente: number
  nombre_cliente: string | null
  correo_cliente: string | null
  fecha_uso: string
  tipo_modalidad: string
  valor_aplicado: number
  creditos_comprados: number | null
  monto_original: number | null
  monto_final: number | null
  id_pago: number | null
}

export interface CostoIaPeriodo {
  id_periodo: number
  fecha_proceso?: string | null
  empresa: string
  cliente: string
  anio: number
  mes: number
  nombre_plan: string
  monto_pagado_usd: number
  creditos_comprados: number
  tokens_entrada_total: number
  tokens_salida_total: number
  costo_ia_usd: number
  margen_usd: number | null
  modelos_usados: string[]
}

export interface UsuarioAdminResponse {
  id: number; nombre: string; correo: string; telefono?: string; id_rol: number
  nombre_rol?: string; estado: string; ultimo_acceso?: string
}

export interface UsuarioAdminCreate {
  nombre: string; correo: string; telefono?: string; id_rol: number
}

export interface RolAdmin {
  id: number; nombre: string; descripcion?: string
  es_superadmin: boolean; estado: string
  usuarios?: number
  permisos?: PermisosMap
}

export type PermisosMap = Record<string, Record<string, boolean>>

export interface RolAdminPayload {
  nombre: string
  descripcion?: string
  es_superadmin?: boolean
  permisos?: PermisosMap
}

export interface CatalogoPais { id: number; codigo_iso: string; nombre: string; codigo_area?: string }
export interface CatalogoMoneda { id: number; codigo_iso: string; nombre: string; simbolo: string; es_base?: boolean }
export interface CatalogoPlanes { id: number; nombre: string; precio_mensual?: number; tipo_precio: string }
export interface CatalogoDocumento { id: number; codigo: string; nombre: string }

export interface ReporteData { name: string; value: number }

export interface AuditoriaEntry {
  id: number
  id_usuario_admin?: number | null
  admin: string
  correo?: string | null
  accion: string
  modulo?: string | null
  entidad?: string | null
  id_entidad?: number | null
  /** JSONB de `saas.s_auditoria` (el backend lo devuelve como objeto). */
  detalle: Record<string, unknown> | string | null
  fecha: string
  ip: string | null
  user_agent?: string | null
}

export interface HistorialEventoListResponse {
  id: number;
  id_cliente: number;
  nombre_cliente: string;
  correo_cliente: string;
  id_empresa: number;
  razon_social_empresa: string;
  anio: number;
  mes: number;
  id_plan: number;
  nombre_plan?: string | null;
  tipo_evento: string;
  // v1.7 — versionado del evento (t_version_carga): permite distinguir un
  // reproceso/reemplazo del primer procesamiento (v1).
  nro_version?: number | null;
  sub_tipo: string | null;
  creditos_cobrados: number;
  /** Cantidad de archivos del snapshot (botón "Ver archivos (N)"). */
  nro_archivos?: number;
  estado_resultado: string;
  fecha_evento: string;
}

/** Archivo del snapshot de un evento de carga (GET /historial-cargas/{id}). */
export interface HistorialArchivo {
  id: number;
  tipo_archivo: string;
  tipo_archivo_nombre?: string | null;
  nombre_archivo: string;
  hash_sha256: string;
  tamano_bytes?: number | null;
  fecha_carga_original: string;
  estado_validacion: string;
}

export interface HistorialEventoDetalleResponse extends HistorialEventoListResponse {
  archivos: HistorialArchivo[];
  kpis: any[];
}

// ── Empresa Emisora ───────────────────────────────────────────────────────────
export interface EmpresaEmisora {
  id: number
  ruc: string
  razon_social: string
  nombre_comercial?: string | null
  direccion_fiscal: string
  ubigeo?: string | null
  departamento: string
  provincia: string
  distrito: string
  telefono?: string | null
  correo?: string | null
  web?: string | null
  logo_url?: string | null
  resolucion_autorizacion?: string | null
  estado: string
  created_at?: string
  updated_at?: string
}

export type EmpresaEmisoraUpdate = Partial<Omit<EmpresaEmisora, 'id' | 'created_at' | 'updated_at'>>

export const empresaEmisoraApi = {
  get: () => api.get<EmpresaEmisora>('/api/pow/empresa-emisora'),
  update: (data: EmpresaEmisoraUpdate) => api.put<EmpresaEmisora>('/api/pow/empresa-emisora', data),
  uploadLogo: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<{ logo_url: string; s3_key: string }>('/api/pow/empresa-emisora/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// ── Mantenimientos Maestros ───────────────────────────────────────────────────
export const mantenimientosApi = {
  list: <T = any>(recurso: string) => api.get<T[]>(`/api/pow/mantenimientos/${recurso}`),
  create: <T = any>(recurso: string, data: any) => api.post<T>(`/api/pow/mantenimientos/${recurso}`, data),
  update: <T = any>(recurso: string, id: number, data: any) => api.put<T>(`/api/pow/mantenimientos/${recurso}/${id}`, data),
  toggleEstado: (recurso: string, id: number, estado?: string) =>
    api.patch<{ id: number; estado: string; activo: boolean }>(`/api/pow/mantenimientos/${recurso}/${id}/estado`, { estado }),
  delete: (recurso: string, id: number) =>
    api.delete<{ ok: boolean; message: string }>(`/api/pow/mantenimientos/${recurso}/${id}`),
  /** Resuelve el impuesto (IGV) vigente por país y fecha (para la emisión). */
  impuestoVigente: (idPais: number, fecha?: string) =>
    api.get<ImpuestoVigente>('/api/pow/mantenimientos/impuesto-vigente', {
      params: { id_pais: idPais, ...(fecha ? { fecha } : {}) },
    }),
}

// ── Invitados (POW Admin) ─────────────────────────────────────────────────────
export interface EmpresaPlanDetalle {
  id_plan: number
  nombre: string
  estado: string
  periodos_cargados: number
}

export interface EmpresaSimple {
  id: number
  id_cliente: number
  razon_social: string
  ruc?: string | null
  tipo_documento?: string | null
  numero_documento?: string | null
  estado: string
  id_pais?: number | null
  pais?: string | null
  ciudad?: string | null
  sector?: string | null
  id_moneda?: number | null
  moneda?: string | null
  responsable?: string | null
  correo_contacto?: string | null
  descripcion?: string | null
  empleados?: string | null
  periodos_cargados?: number
  planes?: EmpresaPlanDetalle[]
}

export interface InvitadoPowCreateData {
  nombre_completo: string
  correo: string
  telefono?: string
  id_cliente: number
  id_empresa: number
  rol?: string
  nivel_permiso?: string
}

export interface InvitadoPowUpdateData {
  nombre_completo: string
  telefono?: string
  id_empresa?: number
}

export const invitadosApi = {
  list: (params?: { id_cliente?: number; estado?: string }) =>
    api.get<any[]>('/api/pow/invitados', { params }),
  crear: (data: InvitadoPowCreateData) =>
    api.post<any>('/api/pow/invitados', data),
  actualizar: (id: number, data: InvitadoPowUpdateData) =>
    api.put<any>(`/api/pow/invitados/${id}`, data),
  toggleEstado: (id: number, estado: string) =>
    api.patch<any>(`/api/pow/invitados/${id}/estado`, null, { params: { estado } }),
  eliminar: (id: number) =>
    api.delete<{ mensaje: string; id: number }>(`/api/pow/invitados/${id}`),
}



